import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Modal, Button } from '../../../components/ui';
import { X, Search, AlertTriangle } from 'lucide-react';
import { fetchAPI } from '../../../services/apiClient';
import { formatTimeCR } from '../../../utils/date';
import { formatTicketForPrint } from '../../../utils/ticketFormat';
import { procesarReimpresion, TicketPayload, TicketJugada } from '../../../services/ticketService';

interface TicketResumen {
  id: string;
  total: number;
  metodo_pago: string | null;
  estado: string;
  created_at: string;
  ticket_number?: string | null;
  jugadas: Array<{ num1: string; num2?: string; monto: number; estado: string }>;
}

interface ReimpresionResponse {
  ticket_id: string;
  ticket_number: string;
  estado: string;
  total: number;
  metodo_pago: string;
  fecha: string;
  cliente_nombre: string;
  referencia: string | null;
  sorteo_nombre: string;
  sorteo_cierre: string;
  sorteo_fecha: string;
  jugadas: Array<{ num1: string; num2?: string | null; monto: number }>;
  local?: {
    nombre: string;
    descripcion?: string | null;
    telefono?: string | null;
    logo?: string | null;
    es_propio?: boolean;
    grupo_id?: string | null;
  };
  es_propio?: boolean;
  es_reimpresion: boolean;
  fecha_anulacion?: string | null;
}

interface AnularTicketModalProps {
  isOpen: boolean;
  onClose: () => void;
  sorteoId: string;
  usuarioId: string;
  sorteoEstado?: string;
  sorteoNombre?: string;
  sorteoCierre?: string;
  onTicketAnulado?: (payload: TicketPayload) => void;
}

export const AnularTicketModal: React.FC<AnularTicketModalProps> = ({
  isOpen,
  onClose,
  sorteoId,
  usuarioId,
  sorteoNombre,
  sorteoCierre,
  onTicketAnulado,
}) => {
  const [tickets, setTickets] = useState<TicketResumen[]>([]);
  const [loading, setLoading] = useState(false);
  const [busqueda, setBusqueda] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [anulando, setAnulando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [exito, setExito] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Búsqueda directa en backend cuando el ticket no está en los últimos 10
  const [ticketDirecto, setTicketDirecto] = useState<TicketResumen | null>(null);
  const [buscandoDirecto, setBuscandoDirecto] = useState(false);
  const [errorDirecto, setErrorDirecto] = useState<string | null>(null);
  const busquedaDirectaTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Limpiar resultado de búsqueda directa cada vez que cambia la búsqueda o la lista
  useEffect(() => {
    setTicketDirecto(null);
    setErrorDirecto(null);
    if (busquedaDirectaTimerRef.current) {
      clearTimeout(busquedaDirectaTimerRef.current);
    }
  }, [busqueda, tickets]);

  // Cargar solo tickets anulables al abrir
  useEffect(() => {
    if (!isOpen || !sorteoId) return;
    setError(null);
    setExito(null);
    setConfirmId(null);
    setBusqueda('');
    cargarTickets();
    setTimeout(() => inputRef.current?.focus(), 100);
  }, [isOpen, sorteoId]);

  // Búsqueda directa en backend cuando no hay coincidencia local.
  // Se activa cuando la búsqueda tiene ≥7 chars (mínimo ticket corto)
  // y la lista local no retorna nada.
  useEffect(() => {
    const q = busqueda.trim();

    // No buscar si la query es muy corta o si hay match local
    if (!q || q.length < 7) {
      setBuscandoDirecto(false);
      return;
    }

    const qLower = q.toLowerCase();
    const hayMatchLocal = tickets.some(t => {
      const idLower = t.id.toLowerCase();
      const largoLower = (t.ticket_number || '').toLowerCase();
      const cortoLower = t.ticket_number
        ? formatTicketForPrint(t.ticket_number).toLowerCase()
        : '';
      return (
        idLower.includes(qLower) ||
        largoLower.includes(qLower) ||
        cortoLower.includes(qLower)
      );
    });

    if (hayMatchLocal) {
      setBuscandoDirecto(false);
      return;
    }

    // No hay match local → consultar backend (con debounce 400ms)
    setBuscandoDirecto(true);
    if (busquedaDirectaTimerRef.current) clearTimeout(busquedaDirectaTimerRef.current);
    busquedaDirectaTimerRef.current = setTimeout(async () => {
      try {
        const data = await fetchAPI<TicketResumen>(
          `/tickets/buscar-anulable/${encodeURIComponent(q)}`
        );
        setTicketDirecto(data);
        setErrorDirecto(null);
      } catch (e: any) {
        setTicketDirecto(null);
        setErrorDirecto(e.message || 'Ticket no encontrado o no anulable.');
      } finally {
        setBuscandoDirecto(false);
      }
    }, 400);

    return () => {
      if (busquedaDirectaTimerRef.current) clearTimeout(busquedaDirectaTimerRef.current);
    };
  }, [busqueda, tickets]);

  const cargarTickets = async () => {
    setLoading(true);
    try {
      // solo_anulables=true: backend filtra por estado ABIERTO,
      // sorteo no cerrado, dentro de hora_cierre, sin jugadas en lista
      const data = await fetchAPI<TicketResumen[]>(
        `/tickets/ultimos?sorteo_id=${encodeURIComponent(sorteoId)}&limit=10&solo_anulables=true`
      );
      setTickets(data);
    } catch {
      setTickets([]);
    } finally {
      setLoading(false);
    }
  };

  // La búsqueda manual del modal Anular DEBE usar el mismo resolver que Jalar
  // (_resolver_ticket_corto, umbral >=7, ventana 3 meses). Si diverge, los tickets
  // visibles en la lista de anulables dejan de poder anularse manualmente
  // (bug reportado abril 2026).
  //
  // Aquí la búsqueda es 100% local sobre la lista de anulables (ya pre-filtrada
  // por backend: banca, local, ventana, estado). Para mantener "una sola fuente
  // de verdad", el match se hace contra TODAS las representaciones que la UI
  // expone: id crudo, ticket_number largo y ticket_number corto (lo que se
  // muestra en pantalla via formatTicketForPrint). Sin esto, escribir el corto
  // visible falla porque el largo contiene los 2 dígitos del año en medio.
  const ticketsFiltrados = busqueda.trim()
    ? (() => {
        const q = busqueda.trim().toLowerCase();
        return tickets.filter(t => {
          const idLower = t.id.toLowerCase();
          const largoLower = (t.ticket_number || '').toLowerCase();
          const cortoLower = t.ticket_number
            ? formatTicketForPrint(t.ticket_number).toLowerCase()
            : '';
          return (
            idLower.includes(q) ||
            (largoLower && largoLower.includes(q)) ||
            (cortoLower && cortoLower.includes(q))
          );
        });
      })()
    : tickets;

  // Combinar lista local con resultado de búsqueda directa.
  // ticketDirecto solo aparece cuando la lista local no tiene coincidencias.
  const ticketsParaMostrar: TicketResumen[] =
    ticketsFiltrados.length > 0
      ? ticketsFiltrados
      : ticketDirecto
        ? [ticketDirecto]
        : [];

  const handleAnular = async (ticketId: string) => {
    setAnulando(true);
    setError(null);
    setExito(null);
    try {
      const ticket = tickets.find(t => t.id === ticketId) ?? (ticketDirecto?.id === ticketId ? ticketDirecto : undefined);
      const displayName = ticket?.ticket_number ? formatTicketForPrint(ticket.ticket_number) : ticketId;
      await fetchAPI<any>(`/tickets/${encodeURIComponent(ticketId)}/anular`, {
        method: 'POST',
        body: JSON.stringify({ usuario_id: usuarioId }),
      });
      setExito(`Ticket ${displayName} anulado correctamente`);
      setConfirmId(null);

      // Try to build comprobante payload for auto-open
      if (onTicketAnulado && ticket) {
        let payload: TicketPayload | null = null;

        // Attempt 1: full data from reimpresion endpoint
        try {
          const resp = await fetchAPI<ReimpresionResponse>(
            `/tickets/${encodeURIComponent(ticketId)}/reimpresion`,
            { method: 'POST' },
          );
          const jugadas: TicketJugada[] = resp.jugadas.map(j => ({
            num1: j.num1,
            num2: j.num2 || undefined,
            monto: j.monto,
          }));
          payload = await procesarReimpresion({
            ticketCode: resp.ticket_number,
            ticketId: resp.ticket_number,
            sorteoNombre: resp.sorteo_nombre,
            sorteoCierre: resp.sorteo_cierre,
            sorteoFecha: resp.sorteo_fecha,
            metodoPago: resp.metodo_pago,
            referencia: resp.referencia,
            total: resp.total,
            estado: resp.estado,
            fechaAnulacion: resp.fecha_anulacion || new Date().toISOString(),
            jugadas,
            local: {
              nombre: resp.local?.nombre || '',
              descripcion: resp.local?.descripcion || null,
              telefono: resp.local?.telefono || null,
              logo: resp.local?.logo || null,
              esPropio: resp.local?.es_propio === true,
              grupoId: resp.local?.grupo_id || null,
            },
            esPropio: resp.local?.es_propio === true,
            clienteNombre: resp.cliente_nombre || 'Cliente',
          });
        } catch {
          // Attempt 2: fallback with local data
          console.warn('[AnularTicket] Reimpresion endpoint failed, using local fallback');
          const jugadasLocal: TicketJugada[] = (ticket.jugadas || []).map(j => ({
            num1: j.num1,
            num2: j.num2 || undefined,
            monto: j.monto,
          }));
          payload = await procesarReimpresion({
            ticketCode: ticket.ticket_number || ticket.id,
            ticketId: ticket.ticket_number || ticket.id,
            sorteoNombre: sorteoNombre || 'Sorteo',
            sorteoCierre: sorteoCierre || '',
            metodoPago: ticket.metodo_pago || 'EFECTIVO',
            total: ticket.total,
            estado: 'ANULADO',
            fechaAnulacion: new Date().toISOString(),
            jugadas: jugadasLocal,
            clienteNombre: 'Cliente',
          });
        }

        // Open comprobante regardless of which path succeeded
        if (payload) {
          onTicketAnulado(payload);
        }
      }

      // Limpiar búsqueda directa y recargar lista
      setTicketDirecto(null);
      setErrorDirecto(null);
      // Recargar lista
      await cargarTickets();
    } catch (e: any) {
      setError(e.message || 'Error al anular ticket');
      setConfirmId(null);
    } finally {
      setAnulando(false);
    }
  };

  const formatHora = (iso: string) => {
    try {
      return formatTimeCR(iso);
    } catch {
      return '';
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Anular Ticket" size="lg">
      <div className="space-y-4">
        {/* Busqueda */}
        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            ref={inputRef}
            type="text"
            value={busqueda}
            onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por número de ticket..."
            className="w-full pl-9 pr-3 py-2 border-2 border-gray-300 rounded-lg focus:border-yellow-500 focus:outline-none text-sm"
            autoComplete="off"
          />
        </div>

        {/* Mensajes */}
        {error && (
          <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}
        {exito && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-3 py-2 rounded text-sm">
            {exito}
          </div>
        )}

        {/* Lista de tickets */}
        <div className="max-h-[50vh] overflow-y-auto space-y-2">
          {loading ? (
            <p className="text-center text-gray-500 py-8 text-sm">Cargando...</p>
          ) : buscandoDirecto ? (
            <p className="text-center text-gray-500 py-8 text-sm">Buscando...</p>
          ) : errorDirecto ? (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 text-amber-700 px-3 py-2 rounded text-sm">
              <AlertTriangle size={16} />
              {errorDirecto}
            </div>
          ) : ticketsParaMostrar.length === 0 ? (
            <p className="text-center text-gray-500 py-8 text-sm">
              {busqueda
                ? 'No se encontraron tickets'
                : 'No hay tickets anulables disponibles'}
            </p>
          ) : (
            ticketsParaMostrar.map(ticket => (
              <div
                key={ticket.id}
                className="border rounded-lg p-3 transition-all bg-white border-gray-200 hover:border-gray-300"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-bold text-gray-900 truncate">
                        {ticket.ticket_number ? formatTicketForPrint(ticket.ticket_number) : ticket.id}
                      </span>
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded bg-green-100 text-green-800">
                        {ticket.estado}
                      </span>
                    </div>
                    <div className="flex items-center gap-3 mt-1 text-xs text-gray-500">
                      <span>{formatHora(ticket.created_at)}</span>
                      <span>{ticket.metodo_pago || '-'}</span>
                      <span>{ticket.jugadas?.length || 0} jugadas</span>
                      <span className="font-mono font-bold text-gray-700">
                        ${typeof ticket.total === 'number' ? ticket.total.toLocaleString() : ticket.total}
                      </span>
                    </div>
                  </div>

                  <div className="flex-shrink-0 ml-2">
                    {confirmId === ticket.id ? (
                      <div className="flex items-center gap-1">
                        <Button
                          variant="danger"
                          size="sm"
                          onClick={() => handleAnular(ticket.id)}
                          disabled={anulando}
                          isLoading={anulando}
                        >
                          Confirmar
                        </Button>
                        <button
                          onClick={() => setConfirmId(null)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <X size={16} />
                        </button>
                      </div>
                    ) : (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          setConfirmId(ticket.id);
                          setError(null);
                          setExito(null);
                        }}
                        className="text-red-600 border-red-300 hover:bg-red-50"
                      >
                        Anular
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Boton cerrar */}
        <div className="flex justify-end pt-2">
          <Button variant="secondary" onClick={onClose}>
            Cerrar
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default AnularTicketModal;
